import { __ } from '../../utils';
import type { IFeedItem } from '../FeedItem/FeedItem';

/**
 * Stavy podnetov tak, ako ich na hlavnej stránke vidí občan.
 *
 * **Zámerne sa nezhodujú s administráciou.** Tam ich kreslí
 * `getReportStatusMeta`. **Od septembra 2026 sú farby zjednotené**: „Nové" /
 * „Podané" je červené a „V riešení" modré na oboch stranách. Zostáva rozdiel
 * v názvosloví bunky („Nové" pre občana, „Podané" pre úradníka) a v tom, že
 * administrácia má navyše „Duplikát" a „Presunuté". Semafor na strane občana
 * tým zanikol - bolo to vedomé rozhodnutie zo zadania.
 *
 * `duplicate` a `moved` sem nepatria - sú to interné rozhodnutia úradu a občan
 * ich v prehľade nefiltruje. Padnú do „Nové", lebo z jeho pohľadu sa podnet
 * stále nevyriešilo.
 */
export type FeedStatKey = 'open' | 'accepted' | 'resolved' | 'mine';

/**
 * To, čo položka feedu nesie ako svoju farbu: tri stavy podnetu a novinka.
 * Nie je to to isté ako `FeedStatKey` - „Moje" je pohľad na zoznam, nie stav,
 * takže vlastnú farbu nemá a v tomto type nie je.
 */
export type FeedMarkerKind = Exclude<FeedStatKey, 'mine'> | 'news';

/**
 * Farba stavu ako **kľúč palety** - nie hex a nie hotová cesta.
 *
 * Je to jediné miesto, kde je napísané, akou farbou sa ktorý stav kreslí.
 * Legenda a štatistika si z neho vyrobia cestu do `sx` (`primary.main`), mapa
 * si tú istú vetvu vytiahne z `theme.palette` ako hex do symbolu značky. Kým
 * boli tie sady dve - `config.CUSTOM_COLOR` pre guličky na mape a cesty do
 * palety pre legendu pod nimi - hovorila každá inou farbou o tom istom stave:
 * legenda mala „Nové" červené `#F73533` z témy, gulička na mape `#E64137`
 * odnikiaľ, a „Vyriešené" bolo v legende `#238528`, na mape `#0E8442`.
 *
 * **„V riešení" je od septembra 2026 modrá, nie žltá** - zjednotené so zoznamom
 * v administrácii, kde ten istý stav modrý vždy bol. Semafor tým na strane
 * občana zaniká; bolo to vedomé rozhodnutie, aby obe obrazovky hovorili o tom
 * istom stave rovnakou farbou.
 *
 * `news` medzi nimi nie je stav, ale druhá vetva feedu. **Musela sa preto
 * presunúť na žltú**, ktorú „V riešení" uvoľnilo: `info` a `blue` boli v téme
 * knižnice tá istá rodina, takže by novinka a rozrobený podnet mali na mape
 * nerozoznateľnú guličku. Šiesta farba do palety nejde - manuál mesta ich má
 * päť (červená, žltá, zelená, modrá, krémová) a `success`/`warning`/`error`/
 * `info` sú len ich aliasy.
 *
 * Od tej istej úpravy sa tie dve vetvy **rozchádzajú o krok**: `theme.ts` appky
 * posúva `blue` na svetlejší krok manuálu (`#4598C4`), `info` necháva tak.
 * Gulička na mape je preto svetlejšia než štítok „V riešení" na karte, a je to
 * zámer - dôvod (kontrast 12px textu) je v komentári pri tom prepise. Rodina je
 * jedna, mení sa len to, ktorý jej schod plocha unesie.
 */
export const FEED_COLOR: Record<
  FeedMarkerKind,
  'primary' | 'yellow' | 'green' | 'blue'
> = {
  open: 'primary',
  accepted: 'blue',
  resolved: 'green',
  news: 'yellow',
};

export type FeedStatFamily = (typeof FEED_COLOR)[FeedMarkerKind];

export const FEED_STATS: {
  key: FeedStatKey;
  label: string;
  /**
   * **Rodina farby v téme, nie hotová cesta ani hex.** Bunka aj legenda si z nej
   * vyrobia to svoje: guličku (`main`), plochu zapnutej bunky (tint nad `main`)
   * a text v nej (`extraDark`). `mine` ju nemá: nie je to stav, ale prienik
   * naprieč všetkými troma, takže vlastná gulička by predstierala štvrtý
   * stupeň semafora.
   *
   * Boli tu dve hotové cesty do palety (`color`, `surface`) a plochu zapnutej
   * bunky nesie `<rodina>.surface`. **Tie tinty sú ladené pre štítok na sivom
   * plátne, nie pre bunku na bielej**: zapnuté „Nové" bolo `#FFF5F5`
   * na `#FFFFFF`, teda 1,03:1, a nebolo vidieť, že filter je zapnutý. Preto
   * odtiaľ ide do bunky rodina a plochu si miesa sama - viď `ACTIVE_TINT`
   * v [[FeedStats]].
   */
  family?: FeedStatFamily;
}[] = [
  { key: 'open', label: __('Nové'), family: FEED_COLOR.open },
  { key: 'accepted', label: __('V riešení'), family: FEED_COLOR.accepted },
  { key: 'resolved', label: __('Vyriešené'), family: FEED_COLOR.resolved },
  { key: 'mine', label: __('Moje') },
];

/**
 * Čo položka nesie ako farbu - stav podnetu, alebo „novinka".
 *
 * Je to tá istá vetva, akou triedi `matchesStat`, a musí ňou zostať: značka na
 * mape, riadok legendy a bunka štatistiky hovoria o tom istom podnete
 * a nesmú sa rozísť. `duplicate` a `moved` padnú do `open` - z pohľadu občana
 * sa podnet stále nevyriešil.
 */
export const feedMarkerKind = (item: IFeedItem): FeedMarkerKind => {
  if (item.type === 'news') return 'news';
  if (item.status === 'resolved') return 'resolved';
  if (item.status === 'accepted') return 'accepted';
  return 'open';
};

const isMine = (item: IFeedItem) => item.type === 'userReport';

/**
 * Patrí položka do bunky štatistiky?
 *
 * **Bunky sú od septembra 2026 štyri oddelené kôpky, nie štyri pohľady na ten
 * istý zoznam.** Vlastný podnet padne **len** do „Moje" a do „Nové" /
 * „V riešení" / „Vyriešené" sa nezapočíta. Dovtedy sa počítal dvakrát a bunky
 * sa prekrývali; zadanie to otočilo, aby „Moje" bolo samostatný filter
 * a stavové bunky hovorili o podnetoch ostatných.
 *
 * Drží sa to na tom, že typ položky prežije zlúčenie zoznamov: vlastný podnet
 * príde aj z verejného zoznamu a `getFeed` mu v takom prípade prepisuje typ na
 * `userReport` práve preto, aby ho táto funkcia videla ako môj.
 */
export const matchesStat = (item: IFeedItem, key: FeedStatKey) => {
  if (item.type === 'news') return false;
  if (key === 'mine') return isMine(item);
  return !isMine(item) && feedMarkerKind(item) === key;
};

export const countStat = (items: IFeedItem[], key: FeedStatKey) =>
  items.reduce((total, item) => total + (matchesStat(item, key) ? 1 : 0), 0);

/**
 * Prejde položka aspoň jednou zapnutou bunkou?
 *
 * Prázdny výber znamená celý zoznam - a **zapnutie všetkých štyroch buniek
 * vyjde naprosto rovnako**, lebo odkedy sú kôpky oddelené (`matchesStat`),
 * pokrývajú spolu každý podnet: vlastné padnú do `mine`, cudzie do jednej
 * z troch stavových. Zvláštna vetva „všetky = žiadne" preto netreba a nesmie
 * sa dopisovať: bola by to druhá definícia toho istého pravidla, ktorá by sa
 * pri pridaní piatej bunky s prvou rozišla.
 */
export const matchesStats = (item: IFeedItem, keys: FeedStatKey[]) =>
  keys.length === 0 || keys.some((key) => matchesStat(item, key));

/**
 * Štítok položky v karte zoznamu.
 *
 * Tóny sú **tie isté farby, aké má značka na mape** (`FEED_COLOR`), len cez
 * aliasy MUI: `error` = `primary` (červená), `info` = `blue`, `success` =
 * `green`, `warning` = `yellow` - v téme sú to zhodné hexy. Keď sa mení
 * `FEED_COLOR`, musí sa s ním pohnúť aj tento zoznam, inak bude karta hovoriť
 * inou farbou než gulička nad ňou.
 *
 * Novinka nie je fialová, ako žiadal dizajnový balík: paleta systému má päť
 * farieb z manuálu mesta a fialová medzi nimi nie je. Šiesta farba kvôli
 * jednému štítku na jednej obrazovke by sa musela pretiahnuť celou témou
 * (`surface`, `contrastText`, tón `StatusChip`-u) a v manuáli by nemala oporu.
 */
export const feedItemBadge = (
  item: IFeedItem,
): { label: string; tone: 'error' | 'warning' | 'success' | 'info' } => {
  if (item.type === 'news') return { label: __('Novinka'), tone: 'warning' };
  if (item.status === 'resolved')
    return { label: __('Vyriešené'), tone: 'success' };
  if (item.status === 'accepted')
    return { label: __('V riešení'), tone: 'info' };
  return { label: __('Nové'), tone: 'error' };
};
